import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  List,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  CheckOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import {
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatDay,
  formatEuro
} from "../workReports/format";
import { shrinkPhoto } from "../workReports/photos";
import {
  addClaim,
  claimPhotoUrl,
  deleteClaim,
  deleteWarranty,
  getWarranty,
  recordInspection,
  updateClaim,
  updateWarranty,
  uploadClaimPhoto
} from "./api";
import { FormDialog } from "./AcceptanceDialogs";
import { warrantyPeriod } from "./calc";
import {
  CLAIM_COLORS,
  GUARANTEE_COLORS,
  STATE_COLORS,
  basisLabels,
  categoryLabels,
  claimStatusLabels,
  guaranteeStatusLabels,
  guaranteeTypeLabels,
  stateLabels
} from "./labels";
import useMeta from "./useMeta";
import "./warranty.css";

const iso = value => (value ? value.format("YYYY-MM-DD") : "");
const day = value => (value ? dayjs(value) : null);

const ClaimDialog = ({ open, claim, warranty, onCancel, onSubmit }) => {
  const { t, i18n } = useTranslation("dashboard");
  const statuses = claimStatusLabels(t);
  const categories = categoryLabels(t);
  return (
    <FormDialog
      open={open}
      width={680}
      title={claim ? t("Claim no. {{n}}", { n: claim.number }) : t("New defect claim")}
      initialValues={{
        description: claim?.description || "",
        location: claim?.location || "",
        period: claim?.period || undefined,
        receivedDate: day(claim?.receivedDate) || dayjs(),
        receivedVia: claim?.receivedVia || "letter",
        writtenNotice: claim ? claim.writtenNotice : true,
        deadline: day(claim?.deadline) || dayjs().add(14, "day"),
        responsible: claim?.responsible || "",
        status: claim?.status || "open",
        cost: claim ? centsToEuro(claim.cost) : null,
        fixedOn: day(claim?.fixedOn),
        fixAcceptedDate: day(claim?.fixAcceptedDate),
        resolutionNote: claim?.resolutionNote || ""
      }}
      onCancel={onCancel}
      onSubmit={values =>
        onSubmit({
          ...values,
          period: values.period || null,
          receivedDate: iso(values.receivedDate),
          deadline: iso(values.deadline),
          fixedOn: iso(values.fixedOn),
          fixAcceptedDate: iso(values.fixAcceptedDate),
          cost: euroToCents(values.cost)
        })
      }
    >
      <Form.Item
        name="description"
        label={t("Defect")}
        rules={[{ required: true, whitespace: true }]}
      >
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={4000} />
      </Form.Item>
      <Row gutter={[12, 0]}>
        <Col xs={24} md={12}>
          <Form.Item name="location" label={t("Location or part")}>
            <Input maxLength={300} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="period" label={t("Warranty period")}>
            <Select
              allowClear
              options={(warranty?.periods || []).map(p => ({
                value: p._id,
                label: p.label || categories[p.category]
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={8}>
          <Form.Item name="receivedDate" label={t("Received on")} rules={[{ required: true }]}>
            <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
          </Form.Item>
        </Col>
        <Col xs={12} md={8}>
          <Form.Item name="receivedVia" label={t("Received by")}>
            <Select
              options={[
                { value: "letter", label: t("Letter") },
                { value: "email", label: t("Email") },
                { value: "phone", label: t("Phone") },
                { value: "onsite", label: t("On site") },
                { value: "other", label: t("Other") }
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="deadline" label={t("Deadline to fix")}>
            <DatePicker className="wty-full" format="DD.MM.YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="writtenNotice"
            valuePropName="checked"
            extra={
              warranty?.contractBasis === "vob"
                ? t("A written claim gives this defect its own two years (§ 13 (5) 1 VOB/B).")
                : undefined
            }
          >
            <Checkbox>{t("Claimed in writing")}</Checkbox>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="responsible" label={t("Responsible")}>
            <Input maxLength={160} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="status" label={t("Status")}>
            <Select
              options={Object.entries(statuses).map(([value, label]) => ({ value, label }))}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={8}>
          <Form.Item name="cost" label={t("Our cost")}>
            <InputNumber
              className="wty-full"
              min={0}
              precision={2}
              addonAfter="€"
              {...decimalInputProps(i18n.language)}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={8}>
          <Form.Item name="fixedOn" label={t("Fixed on")}>
            <DatePicker className="wty-full" format="DD.MM.YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="fixAcceptedDate" label={t("Fix accepted on")}>
            <DatePicker className="wty-full" format="DD.MM.YYYY" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="resolutionNote" label={t("Resolution or reason for rejection")}>
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={4000} />
      </Form.Item>
    </FormDialog>
  );
};

const WarrantyDetail = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const history = useHistory();
  const [meta] = useMeta();
  const [warranty, setWarranty] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [claimDialog, setClaimDialog] = useState(null);
  const [inspection, setInspection] = useState(null);
  const photoInput = useRef(null);
  const photoTarget = useRef(null);

  const apply = data => {
    setWarranty(data);
    setPeriods(data.periods.map(p => ({ ...p })));
    setDirty(false);
  };

  useEffect(() => {
    getWarranty(id)
      .then(res => apply(res.data.data))
      .catch(generalErrorAlert);
  }, [id]);

  if (!warranty || !meta) return <Skeleton active paragraph={{ rows: 10 }} />;
  const canUpdate = meta.can.update;
  const categories = categoryLabels(t);
  const claims = claimStatusLabels(t);
  const states = stateLabels(t);
  const summary = warranty.summary;

  const call = async (fn, message) => {
    try {
      const res = await fn();
      apply(res.data.data);
      if (message) Success(message);
    } catch (error) {
      generalErrorAlert(error);
      throw error;
    }
  };

  const setPeriod = (index, patch) => {
    setPeriods(prev => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
    setDirty(true);
  };

  const addPhoto = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !photoTarget.current) return;
    try {
      const { blob } = await shrinkPhoto(file);
      await call(() => uploadClaimPhoto(id, photoTarget.current, blob));
    } catch {
      // reported
    }
  };

  return (
    <div className="warranty-page">
      <input
        ref={photoInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={addPhoto}
      />
      <Card className="mb-3">
        <div className="wty-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label={t("Back")}
            onClick={() => history.push("/user/warranty?tab=warranties")}
          />
          <div className="wty-header-title">
            <Typography.Title level={4}>{warranty.title}</Typography.Title>
            <Space size={4} wrap>
              <Tag color={STATE_COLORS[summary.state]}>{states[summary.state]}</Tag>
              <Tag>{basisLabels(t)[warranty.contractBasis]}</Tag>
            </Space>
          </div>
          <Space wrap className="wty-header-actions">
            {canUpdate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setClaimDialog({ claim: null })}
              >
                {t("New defect claim")}
              </Button>
            )}
            {!warranty.acceptance && meta.can.delete && !warranty.claims.length && (
              <Popconfirm
                title={t("Delete this warranty?")}
                onConfirm={() =>
                  deleteWarranty(id)
                    .then(() => history.push("/user/warranty?tab=warranties"))
                    .catch(generalErrorAlert)
                }
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t("Delete")}
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>
        <div className="wty-small mt-2">
          {[
            warranty.projectInfo?.name,
            warranty.clientInfo?.name,
            summary.start
              ? t("accepted on {{date}}", { date: formatDay(summary.start) })
              : t("Acceptance not effective"),
            summary.end && t("ends {{date}}", { date: formatDay(summary.end) })
          ]
            .filter(Boolean)
            .join(" · ")}
          {warranty.acceptanceInfo && (
            <>
              {" · "}
              <Link to={`/user/warranty/acceptances/${warranty.acceptance}`}>
                {warranty.acceptanceInfo.number}
              </Link>
            </>
          )}
        </div>
        {!summary.start && (
          <Alert
            className="mt-2"
            type="warning"
            showIcon
            message={t(
              "The acceptance protocol was reopened. The warranty starts again with its acceptance."
            )}
          />
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={t("Warranty periods")}
            className="mb-3"
            extra={
              canUpdate && (
                <Space>
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setPeriods(prev => [
                        ...prev,
                        { category: "other_work", label: "", months: 0, start: "" }
                      ]);
                      setDirty(true);
                    }}
                  >
                    {t("Add")}
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<SaveOutlined />}
                    disabled={!dirty}
                    onClick={() =>
                      call(() => updateWarranty(id, { periods }), t("Saved")).catch(() => {})
                    }
                  >
                    {t("Save")}
                  </Button>
                </Space>
              )
            }
          >
            <List
              dataSource={periods}
              renderItem={(p, index) => {
                // Live preview while editing (the server adds the public holidays for § 193 BGB)
                const preview = warrantyPeriod({
                  category: p.category,
                  months: p.months,
                  start: p.start && p.start > summary.start ? p.start : summary.start,
                  contractBasis: warranty.contractBasis
                });
                const computed = summary.periods.find(s => s._id === p._id);
                return (
                  <List.Item>
                    <Row gutter={[8, 8]} className="wty-full">
                      <Col xs={24} md={14}>
                        <Select
                          className="wty-full"
                          disabled={!canUpdate}
                          value={p.category}
                          onChange={value => setPeriod(index, { category: value })}
                          options={Object.entries(categories).map(([value, label]) => ({
                            value,
                            label
                          }))}
                        />
                      </Col>
                      <Col xs={24} md={10}>
                        <Input
                          disabled={!canUpdate}
                          placeholder={t("Label, e.g. heating system")}
                          value={p.label}
                          onChange={e => setPeriod(index, { label: e.target.value })}
                        />
                      </Col>
                      <Col xs={12} md={8}>
                        <InputNumber
                          className="wty-full"
                          disabled={!canUpdate}
                          min={0}
                          max={360}
                          precision={0}
                          addonAfter={t("months")}
                          placeholder={
                            preview.statutoryMonths ? String(preview.statutoryMonths) : ""
                          }
                          value={p.months || null}
                          onChange={value => setPeriod(index, { months: value || 0 })}
                        />
                      </Col>
                      <Col xs={12} md={8}>
                        <DatePicker
                          className="wty-full"
                          disabled={!canUpdate}
                          format="DD.MM.YYYY"
                          placeholder={t("Later start")}
                          value={day(p.start)}
                          onChange={value => setPeriod(index, { start: iso(value) })}
                        />
                      </Col>
                      <Col xs={24} md={8}>
                        <div>
                          <Typography.Text strong>{formatDay(preview.end) || "—"}</Typography.Text>{" "}
                          {computed && (
                            <Tag color={STATE_COLORS[computed.state]}>{states[computed.state]}</Tag>
                          )}
                        </div>
                        <div className="wty-small">
                          {preview.agreed ? t("agreed") : preview.source}
                          {computed?.endShifted && computed.endShifted !== computed.end
                            ? ` · ${t("claims possible until {{date}} (§ 193 BGB)", { date: formatDay(computed.endShifted) })}`
                            : ""}
                        </div>
                      </Col>
                      {canUpdate && periods.length > 1 && (
                        <Col xs={24}>
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setPeriods(prev => prev.filter((_, i) => i !== index));
                              setDirty(true);
                            }}
                          >
                            {t("Remove")}
                          </Button>
                        </Col>
                      )}
                    </Row>
                  </List.Item>
                );
              }}
            />
          </Card>

          <Card title={t("Inspections before the end")} className="mb-3">
            {summary.inspections.length ? (
              <List
                size="small"
                dataSource={summary.inspections}
                renderItem={i => {
                  const done = (warranty.inspections || []).find(x => x.end === i.end && x.doneOn);
                  return (
                    <List.Item
                      actions={
                        canUpdate && !done
                          ? [
                              <Button
                                key="done"
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={() => setInspection(i)}
                              >
                                {t("Held")}
                              </Button>
                            ]
                          : []
                      }
                    >
                      <div>
                        <Typography.Text strong>
                          {t("from {{date}}", { date: formatDay(i.date) })}
                        </Typography.Text>{" "}
                        <span className="wty-small">
                          {t("before the end on {{date}}", { date: formatDay(i.end) })} ·{" "}
                          {i.labels.map(l => categories[l] || l).join(", ")}
                        </span>
                        {done && (
                          <div className="wty-small">
                            {t("Held on {{date}}", { date: formatDay(done.doneOn) })}
                            {done.note ? `: ${done.note}` : ""}
                          </div>
                        )}
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          <Card title={t("Guarantee bonds")} className="mb-3">
            {warranty.guarantees.length ? (
              <List
                size="small"
                dataSource={warranty.guarantees}
                renderItem={g => (
                  <List.Item>
                    <Link to={`/user/warranty?tab=guarantees&guarantee=${g._id}`}>
                      {guaranteeTypeLabels(t)[g.type]} {g.bondNumber || g.number}
                    </Link>
                    <Space size={4}>
                      <span>{formatEuro(g.amount, lng)}</span>
                      <Tag color={GUARANTEE_COLORS[g.status]}>
                        {guaranteeStatusLabels(t)[g.status]}
                      </Tag>
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("No guarantee bond for this warranty")}
              />
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={t("Defect claims during the warranty")}
            className="mb-3"
            extra={
              summary.claimCost > 0 &&
              t("Cost {{amount}}", { amount: formatEuro(summary.claimCost, lng) })
            }
          >
            {warranty.claims.length ? (
              <List
                dataSource={[...warranty.claims].reverse()}
                renderItem={c => {
                  const computed = summary.claims.find(x => x._id === c._id) || {};
                  return (
                    <List.Item>
                      <div className="wty-full">
                        <Space wrap size={4}>
                          <Typography.Text strong>
                            {t("No. {{n}}", { n: c.number })}
                          </Typography.Text>
                          <Tag color={CLAIM_COLORS[c.status]}>{claims[c.status]}</Tag>
                          {computed.overdue && <Tag color="error">{t("Overdue")}</Tag>}
                          {computed.lateClaim && (
                            <Tag color="warning">{t("After the warranty ended")}</Tag>
                          )}
                        </Space>
                        <div className="wty-pre">{c.description}</div>
                        <div className="wty-small">
                          {[
                            c.location,
                            t("received {{date}}", { date: formatDay(c.receivedDate) }),
                            c.deadline && t("until {{date}}", { date: formatDay(c.deadline) }),
                            c.cost ? formatEuro(c.cost, lng) : ""
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        {computed.limitationEnd && (
                          <div className="wty-small">
                            {t("Claim time-barred after {{date}}", {
                              date: formatDay(computed.limitationEnd)
                            })}
                            {computed.limitationSource ? ` (${computed.limitationSource})` : ""}
                          </div>
                        )}
                        {c.photos?.length > 0 && (
                          <Image.PreviewGroup>
                            <Space wrap className="wty-photos">
                              {c.photos.map(p => (
                                <Image
                                  key={p._id}
                                  width={72}
                                  height={72}
                                  src={claimPhotoUrl(id, p._id)}
                                  alt=""
                                />
                              ))}
                            </Space>
                          </Image.PreviewGroup>
                        )}
                        {canUpdate && (
                          <Space wrap className="mt-2">
                            <Button size="small" onClick={() => setClaimDialog({ claim: c })}>
                              {t("Edit")}
                            </Button>
                            <Button
                              size="small"
                              icon={<CameraOutlined />}
                              onClick={() => {
                                photoTarget.current = c._id;
                                photoInput.current?.click();
                              }}
                            >
                              {t("Photo")}
                            </Button>
                            {meta.can.delete && (
                              <Popconfirm
                                title={t("Delete this claim?")}
                                onConfirm={() => call(() => deleteClaim(id, c._id)).catch(() => {})}
                              >
                                <Button
                                  size="small"
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  aria-label={t("Delete")}
                                />
                              </Popconfirm>
                            )}
                          </Space>
                        )}
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("No defects claimed")} />
            )}
          </Card>
        </Col>
      </Row>

      <ClaimDialog
        open={Boolean(claimDialog)}
        claim={claimDialog?.claim}
        warranty={warranty}
        onCancel={() => setClaimDialog(null)}
        onSubmit={values =>
          call(
            () =>
              claimDialog.claim
                ? updateClaim(id, claimDialog.claim._id, values)
                : addClaim(id, values),
            t("Saved")
          ).then(() => setClaimDialog(null))
        }
      />
      <FormDialog
        open={Boolean(inspection)}
        title={t("Inspection held")}
        initialValues={{ doneOn: dayjs(), note: "" }}
        onCancel={() => setInspection(null)}
        onSubmit={values =>
          call(
            () =>
              recordInspection(id, {
                end: inspection.end,
                doneOn: iso(values.doneOn),
                note: values.note
              }),
            t("Saved")
          ).then(() => setInspection(null))
        }
      >
        <Form.Item name="doneOn" label={t("Date")} rules={[{ required: true }]}>
          <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        <Form.Item name="note" label={t("Result")}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} />
        </Form.Item>
      </FormDialog>
    </div>
  );
};

export default WarrantyDetail;
